import { useMemo, useRef, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useParams } from 'react-router'

import { Container } from '@/components/Container'
import { allCarriersDeclined, AllDeclinedOverlay } from '@/components/quotes/AllDeclinedOverlay'
import { applicationToQuoteCards, indicationStages } from '@/components/quotes/applicationToQuoteCards'
import {
  type ClaimsCapabilityFilterKey,
  FiltersSection,
  type QuoteSortBy,
} from '@/components/quotes/FiltersSection'
import { QuoteCardsCarousel, type QuoteCarouselHandle } from '@/components/quotes/QuoteCardsCarousel'
import { type CoiSpeed, QuoteCardStage } from '@/components/quotes/quoteCardTypes'
import { filterAndSortQuotes } from '@/components/quotes/quoteFilters'
import {
  quoteListProperties,
  QuotesAnalyticsProvider,
  QuotesEvent,
  QuotesSurface,
  useQuotesPageViewed,
  useQuotesStateShown,
} from '@/components/quotes/quotesAnalytics'
import { TopQuotesHeader } from '@/components/quotes/TopQuotesHeader'
import { usePublicQuotes } from '@/models/publicQuotes'
import { ProductFlowFooter } from '@/routes/Public/marketing/components/ProductFlowFooter'
import { ProductFlowNav } from '@/routes/Public/marketing/components/ProductFlowNav'
import {
  QUOTES_FOOTER_NOTE,
  SITE_FOOTER_LEGAL_LINKS,
} from '@/routes/Public/marketing/content/siteFooterContent'

// Enough to fill the carousel on desktop. The real count isn't known until the fetch lands.
const LOADING_CARD_COUNT = 3

/**
 * The quotes page as shared by link: the same cards, with nothing that would change
 * the application. No re-quote, no uploading, no editing — a reader holding the link
 * isn't the customer, and the endpoint behind it is public.
 */
export function PublicQuotes() {
  const { applicationId } = useParams()

  return (
    <QuotesAnalyticsProvider surface={QuotesSurface.SHARED_LINK} applicationId={applicationId}>
      <PublicQuotesBody />
    </QuotesAnalyticsProvider>
  )
}

function PublicQuotesBody() {
  const { applicationId } = useParams()
  const { quotes: shared, isLoading, isError } = usePublicQuotes(applicationId)

  const [sortBy, setSortBy] = useState<QuoteSortBy>('best')
  const [selectedRatings, setSelectedRatings] = useState<Set<string>>(new Set())
  const [selectedCoiSpeeds, setSelectedCoiSpeeds] = useState<Set<CoiSpeed>>(new Set())
  const [selectedCoverages, setSelectedCoverages] = useState<Set<string>>(new Set())
  const [financingAvailable, setFinancingAvailable] = useState(false)
  const [selectedClaimsCapabilities, setSelectedClaimsCapabilities] = useState<
    Set<ClaimsCapabilityFilterKey>
  >(new Set())
  const carouselRef = useRef<QuoteCarouselHandle>(null)
  // The arrows live in the header, above the scroller they drive, so the carousel
  // reports where it is rather than each of them working it out.
  const [edges, setEdges] = useState({ canScrollPrev: false, canScrollNext: true })

  const requestedCoverages = useMemo(
    () => new Set(shared?.selectedCoverages ?? []),
    [shared?.selectedCoverages]
  )

  // Only what a broker actually flagged is badged — the page never picks one itself.
  const quotes = useMemo(
    () => applicationToQuoteCards(shared?.indications ?? [], shared?.broker?.name),
    [shared?.indications, shared?.broker?.name]
  )

  // Ranking one price against itself says nothing, so the sort waits for a second.
  const pricedQuoteCount = quotes.filter((quote) => quote.stage === QuoteCardStage.PRICED).length
  const noCarrierPriced = allCarriersDeclined(
    indicationStages(shared?.indications ?? []),
    shared?.awaitingCarrierCount ?? 0
  )
  const showsEmptyState =
    !!shared && quotes.length === 0 && shared.awaitingCarrierCount === 0 && !noCarrierPriced

  // Keyed on the payload, not the route param: the id is known before the quotes are.
  useQuotesPageViewed(shared ? applicationId : undefined, {
    ...quoteListProperties(quotes),
    awaiting_carrier_count: shared?.awaitingCarrierCount ?? 0,
    requested_coverages: [...requestedCoverages],
    is_empty: showsEmptyState,
    all_carriers_declined: noCarrierPriced,
    has_matched_broker: !!shared?.broker,
  })
  useQuotesStateShown(QuotesEvent.EMPTY_STATE_SHOWN, showsEmptyState)

  const visibleQuotes = useMemo(
    () =>
      filterAndSortQuotes(quotes, {
        sortBy,
        selectedRatings,
        selectedCoiSpeeds,
        selectedCoverages,
        financingAvailable,
        selectedClaimsCapabilities,
        requestedCoverages,
      }),
    [
      quotes,
      sortBy,
      selectedRatings,
      selectedCoiSpeeds,
      selectedCoverages,
      financingAvailable,
      selectedClaimsCapabilities,
      requestedCoverages,
    ]
  )

  const orderKey = useMemo(
    () =>
      JSON.stringify([
        sortBy,
        [...selectedRatings].sort(),
        [...selectedCoiSpeeds].sort(),
        [...selectedCoverages].sort(),
        financingAvailable,
        [...selectedClaimsCapabilities].sort(),
      ]),
    [
      sortBy,
      selectedRatings,
      selectedCoiSpeeds,
      selectedCoverages,
      financingAvailable,
      selectedClaimsCapabilities,
    ]
  )

  function toggleIn<T>(setter: React.Dispatch<React.SetStateAction<Set<T>>>, value: T) {
    setter((current) => {
      const next = new Set(current)
      if (next.has(value)) next.delete(value)
      else next.add(value)
      return next
    })
  }

  function clearAllFilters() {
    setSelectedRatings(new Set())
    setSelectedCoiSpeeds(new Set())
    setSelectedCoverages(new Set())
    setFinancingAvailable(false)
    setSelectedClaimsCapabilities(new Set())
  }

  return (
    <>
      {/* A shared link shouldn't turn up in search results — it's someone's quotes. */}
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>{shared ? `${shared.businessName} — Quotes` : 'Quotes'}</title>
      </Helmet>
      {/* The matched-broker variant, so a reader knows who to call about these quotes —
          and because the plain variant's divider is border-white, invisible here. */}
      <ProductFlowNav
        hideLogin
        containerAligned
        matchedBroker={
          shared?.broker
            ? {
                name: shared.broker.name,
                phone: shared.broker.phone,
                phoneE164: shared.broker.phoneE164,
                email: shared.broker.email,
                avatarUrl: shared.broker.photoUrl || undefined,
              }
            : undefined
        }
      />
      <Container className="mt-10 flex flex-1 flex-col pb-20">
        {/* Card-shaped, not generic bars — this page is only ever a list of quote cards. */}
        {isLoading && (
          <div className="mt-10">
            <QuoteCardsCarousel
              quotes={[]}
              orderKey="loading"
              awaitingCarrierCount={LOADING_CARD_COUNT}
              isLoading
            />
          </div>
        )}
        {isError && (
          <p className="text-ink-400 py-20 text-center text-sm">
            This link isn&apos;t valid any more. Ask whoever shared it for a new one.
          </p>
        )}
        {shared && (
          <>
            <div className="mb-6">
              <span className="text-ink-400 text-xs font-semibold tracking-wide uppercase">
                Insurance quotes for
              </span>
              <h2 className="text-ink text-xl font-bold">{shared.businessName}</h2>
            </div>
            <FiltersSection
              sortBy={sortBy}
              onSortByChange={setSortBy}
              selectedRatings={selectedRatings}
              onToggleRating={(rating) => toggleIn(setSelectedRatings, rating)}
              onClearRatings={() => setSelectedRatings(new Set())}
              selectedCoiSpeeds={selectedCoiSpeeds}
              onToggleCoiSpeed={(speed) => toggleIn(setSelectedCoiSpeeds, speed)}
              onClearCoiSpeeds={() => setSelectedCoiSpeeds(new Set())}
              selectedCoverages={selectedCoverages}
              onToggleCoverage={(coverage) => toggleIn(setSelectedCoverages, coverage)}
              onClearCoverages={() => setSelectedCoverages(new Set())}
              financingAvailable={financingAvailable}
              onToggleFinancingAvailable={() => setFinancingAvailable((current) => !current)}
              onClearFinancingAvailable={() => setFinancingAvailable(false)}
              selectedClaimsCapabilities={selectedClaimsCapabilities}
              onToggleClaimsCapability={(key) => toggleIn(setSelectedClaimsCapabilities, key)}
              onClearClaimsCapabilities={() => setSelectedClaimsCapabilities(new Set())}
              onClearAllFilters={clearAllFilters}
              isUpdatingQuotes={false}
              sortDisabled={pricedQuoteCount < 2}
            />
            <TopQuotesHeader
              onScrollPrev={() => carouselRef.current?.scrollPrev()}
              onScrollNext={() => carouselRef.current?.scrollNext()}
              canScrollPrev={edges.canScrollPrev}
              canScrollNext={edges.canScrollNext}
              policyExpirationDate={shared.policyExpirationDate ?? undefined}
            />
            {showsEmptyState ? (
              <p className="text-ink-400 py-10 text-center text-sm">No quotes to show yet.</p>
            ) : (
              <QuoteCardsCarousel
                ref={carouselRef}
                quotes={visibleQuotes}
                orderKey={orderKey}
                hideCheckout
                revealFocusedCard
                onEdgeChange={setEdges}
                awaitingCarrierCount={shared.awaitingCarrierCount}
              />
            )}
            {noCarrierPriced && <AllDeclinedOverlay />}
          </>
        )}
      </Container>
      <ProductFlowFooter legalLinks={SITE_FOOTER_LEGAL_LINKS} note={QUOTES_FOOTER_NOTE} />
    </>
  )
}
